import React, { useState, useEffect } from 'react';
import './CalendarBooking.css';

const CalendarBooking = ({ 
  eventType = 'IELTS', 
  onBookingComplete,
  availableSlots = [],
  eventTitle = "IELTS Consultation",
  duration = 60,
  timezone = "Asia/Kuwait",
  restrictedDate = null,
  restrictedTimeSlots = null
}) => {
  const [currentStep, setCurrentStep] = useState('calendar'); // calendar, times, details, confirmation
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(restrictedDate ? new Date(restrictedDate) : new Date());
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [userDetails, setUserDetails] = useState({
    fullName: '',
    email: '',
    phone: '',
    nationality: '',
    age: '',
    studyDestination: 'uk',
    englishLevel: 'average',
    previousIELTS: 'no',
    previousScore: '',
    fieldOfStudy: '',
    specialRequests: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Generate available dates for the next 30 days (excluding weekends)
  // OR use restricted date if provided
  useEffect(() => {
    const generateAvailableDates = () => {
      // If restricted date is provided, only allow that date
      if (restrictedDate) {
        setAvailableDates([new Date(restrictedDate)]);
        return;
      }
      
      // Otherwise, generate dates for next 30 days (excluding weekends)
      const dates = [];
      const today = new Date();
      
      for (let i = 1; i <= 30; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        
        // Skip weekends (Saturday = 6, Sunday = 0)
        if (date.getDay() !== 0 && date.getDay() !== 6) {
          dates.push(new Date(date));
        }
      }
      
      setAvailableDates(dates);
    };

    generateAvailableDates();
  }, [restrictedDate]);

  // Generate available time slots for selected date
  // OR use restricted time slots if provided
  useEffect(() => {
    if (selectedDate) {
      const fetchAvailableSlots = async () => {
        // If restricted time slots are provided, use them
        if (restrictedTimeSlots && restrictedTimeSlots.length > 0) {
          setAvailableTimes(restrictedTimeSlots);
          return;
        }
        
        // Otherwise, fetch or generate time slots
        try {
          const bookingService = (await import('../../Services/bookingService')).default;
          const slots = await bookingService.getAvailableSlots(selectedDate, eventType);
          setAvailableTimes(slots);
        } catch (error) {
          console.error('Failed to fetch available slots:', error);
          // Fallback to generated slots
          const times = [];
          const startHour = 9;
          const endHour = 17;
          
          for (let hour = startHour; hour < endHour; hour++) {
            const time = `${hour.toString().padStart(2, '0')}:00`;
            const timeSlot = {
              time,
              available: Math.random() > 0.3
            };
            times.push(timeSlot);
          }
          
          setAvailableTimes(times);
        }
      };

      fetchAvailableSlots();
    }
  }, [selectedDate, eventType, restrictedTimeSlots]);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    const [hour, minute] = time.split(':');
    const hourNum = parseInt(hour);
    const ampm = hourNum >= 12 ? 'PM' : 'AM';
    const displayHour = hourNum > 12 ? hourNum - 12 : hourNum === 0 ? 12 : hourNum;
    return `${displayHour}:${minute} ${ampm}`;
  };

  const isDateAvailable = (date) => {
    return availableDates.some(availableDate => 
      availableDate.toDateString() === date.toDateString()
    );
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setCurrentStep('times');
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setCurrentStep('details');
  };

  const handleInputChange = (field, value) => {
    setUserDetails(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!userDetails.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!userDetails.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userDetails.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!userDetails.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[+]?[0-9]{8,15}$/.test(userDetails.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (!userDetails.nationality.trim()) {
      newErrors.nationality = 'Nationality is required';
    }
    
    if (!userDetails.age.trim()) {
      newErrors.age = 'Age is required';
    } else if (isNaN(userDetails.age) || parseInt(userDetails.age) < 16 || parseInt(userDetails.age) > 100) {
      newErrors.age = 'Please enter a valid age between 16 and 100';
    }
    
    if (!userDetails.fieldOfStudy.trim()) {
      newErrors.fieldOfStudy = 'Field of study is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBooking = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      const googleSheetsService = (await import('../../Services/googleSheetsService')).default;
      
      const bookingData = {
        eventType,
        eventTitle,
        date: selectedDate,
        time: selectedTime,
        userDetails,
        duration,
        timezone
      };
      
      // Save booking to Google Sheets
      const result = await googleSheetsService.saveBooking(bookingData);
      
      // Track successful booking
      googleSheetsService.trackBookingEvent('booking_completed', bookingData);
      
      setCurrentStep('confirmation');
      
      if (onBookingComplete) {
        // Format data to match IELTS registration schema
        onBookingComplete({
          name: userDetails.fullName,
          age: userDetails.age,
          phoneNumber: userDetails.phone,
          email: userDetails.email,
          nationality: userDetails.nationality,
          studyDestination: userDetails.studyDestination,
          previousIELTS: userDetails.previousIELTS,
          englishLevel: userDetails.englishLevel,
          fieldOfStudy: userDetails.fieldOfStudy,
          time: selectedTime,
          date: selectedDate,
          guests: [],
          confirmationCode: result.confirmationCode,
          status: result.status,
          // Keep original booking data for backward compatibility
          originalBookingData: bookingData
        });
      }
    } catch (error) {
      console.error('Booking failed:', error);
      setErrors({ general: error.message || 'Booking failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isAvailable = isDateAvailable(date);
      const isPast = date < new Date().setHours(0, 0, 0, 0);
      
      days.push(
        <div
          key={day}
          className={`calendar-day ${isAvailable && !isPast ? 'available' : 'unavailable'}`}
          onClick={() => isAvailable && !isPast && handleDateSelect(date)}
        >
          {day}
        </div>
      );
    }
    
    return days;
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  return (
    <div className="calendar-booking">
      <div className="booking-header">
        <h2>{eventTitle}</h2>
        <p className="booking-duration">{duration} minutes</p>
      </div>

      {currentStep === 'calendar' && (
        <div className="calendar-step">
          <div className="calendar-header">
            <button onClick={goToPreviousMonth} className="nav-button">
              ←
            </button>
            <h3>
              {currentMonth.toLocaleDateString('en-US', { 
                month: 'long', 
                year: 'numeric' 
              })}
            </h3>
            <button onClick={goToNextMonth} className="nav-button">
              →
            </button>
          </div>
          
          <div className="calendar-weekdays">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="weekday">{day}</div>
            ))}
          </div>
          
          <div className="calendar-grid">
            {renderCalendar()}
          </div>
          
          <div className="calendar-legend">
            <div className="legend-item">
              <div className="legend-dot available"></div>
              <span>Available</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot unavailable"></div>
              <span>Unavailable</span>
            </div>
          </div>
        </div>
      )}

      {currentStep === 'times' && (
        <div className="times-step">
          <div className="step-header">
            <button 
              onClick={() => setCurrentStep('calendar')} 
              className="back-button"
            >
              ← Back
            </button>
            <div>
              <h3>Select a time</h3>
              <p className="selected-date">{formatDate(selectedDate)}</p>
            </div>
          </div>
          
          <div className="time-slots">
            {availableTimes.map((slot, index) => (
              <button
                key={index}
                className={`time-slot ${slot.available ? 'available' : 'unavailable'}`}
                onClick={() => slot.available && handleTimeSelect(slot.time)}
                disabled={!slot.available}
              >
                {formatTime(slot.time)}
              </button>
            ))}
          </div>
        </div>
      )}

      {currentStep === 'details' && (
        <div className="details-step">
          <div className="step-header">
            <button 
              onClick={() => setCurrentStep('times')} 
              className="back-button"
            >
              ← Back
            </button>
            <div>
              <h3>Enter your details</h3>
              <p className="booking-summary">
                {formatDate(selectedDate)} at {formatTime(selectedTime)}
              </p>
            </div>
          </div>
          
          <form className="booking-form" onSubmit={(e) => e.preventDefault()}>
            <div className="form-group">
              <label htmlFor="fullName">Full Name *</label>
              <input
                type="text"
                id="fullName"
                value={userDetails.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                className={errors.fullName ? 'error' : ''}
                placeholder="Enter your full name"
              />
              {errors.fullName && <span className="error-message">{errors.fullName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                value={userDetails.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={errors.email ? 'error' : ''}
                placeholder="Enter your email address"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                value={userDetails.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={errors.phone ? 'error' : ''}
                placeholder="Enter your phone number"
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="nationality">Nationality *</label>
              <input
                type="text"
                id="nationality"
                value={userDetails.nationality}
                onChange={(e) => handleInputChange('nationality', e.target.value)}
                className={errors.nationality ? 'error' : ''}
                placeholder="Enter your nationality"
              />
              {errors.nationality && <span className="error-message">{errors.nationality}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="age">Age *</label>
              <input
                type="number"
                id="age"
                value={userDetails.age}
                onChange={(e) => handleInputChange('age', e.target.value)}
                className={errors.age ? 'error' : ''}
                placeholder="Enter your age"
                min="16"
                max="100"
              />
              {errors.age && <span className="error-message">{errors.age}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="fieldOfStudy">Field of Study *</label>
              <input
                type="text"
                id="fieldOfStudy"
                value={userDetails.fieldOfStudy}
                onChange={(e) => handleInputChange('fieldOfStudy', e.target.value)}
                className={errors.fieldOfStudy ? 'error' : ''}
                placeholder="e.g., Engineering, Business, Medicine"
              />
              {errors.fieldOfStudy && <span className="error-message">{errors.fieldOfStudy}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="studyDestination">Study Destination</label>
              <select
                id="studyDestination"
                value={userDetails.studyDestination}
                onChange={(e) => handleInputChange('studyDestination', e.target.value)}
              >
                <option value="uk">United Kingdom</option>
                <option value="other">Other Country</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="englishLevel">English Level</label>
              <select
                id="englishLevel"
                value={userDetails.englishLevel}
                onChange={(e) => handleInputChange('englishLevel', e.target.value)}
              >
                <option value="weak">Weak</option>
                <option value="average">Average</option>
                <option value="excellent">Excellent</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="previousIELTS">Have you taken IELTS before?</label>
              <select
                id="previousIELTS"
                value={userDetails.previousIELTS}
                onChange={(e) => handleInputChange('previousIELTS', e.target.value)}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>

            {userDetails.previousIELTS === 'yes' && (
              <div className="form-group">
                <label htmlFor="previousScore">Previous IELTS Score</label>
                <input
                  type="text"
                  id="previousScore"
                  value={userDetails.previousScore}
                  onChange={(e) => handleInputChange('previousScore', e.target.value)}
                  placeholder="e.g., 6.5"
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="specialRequests">Special Requests (Optional)</label>
              <textarea
                id="specialRequests"
                value={userDetails.specialRequests}
                onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                placeholder="Any special requirements or questions?"
                rows="3"
              />
            </div>

            {errors.general && (
              <div className="error-message general-error">
                {errors.general}
              </div>
            )}

            <button
              type="button"
              onClick={handleBooking}
              disabled={isLoading}
              className="book-button"
            >
              {isLoading ? 'Booking...' : 'Confirm Booking'}
            </button>
          </form>
        </div>
      )}

      {currentStep === 'confirmation' && (
        <div className="confirmation-step">
          <div className="confirmation-icon">✓</div>
          <h3>Booking Confirmed!</h3>
          <div className="booking-details">
            <p><strong>Event:</strong> {eventTitle}</p>
            <p><strong>Date:</strong> {formatDate(selectedDate)}</p>
            <p><strong>Time:</strong> {formatTime(selectedTime)}</p>
            <p><strong>Duration:</strong> {duration} minutes</p>
            <p><strong>Name:</strong> {userDetails.fullName}</p>
            <p><strong>Email:</strong> {userDetails.email}</p>
          </div>
          <p className="confirmation-message">
            A confirmation email has been sent to {userDetails.email} with all the details.
          </p>
          <button 
            onClick={() => {
              setCurrentStep('calendar');
              setSelectedDate(null);
              setSelectedTime(null);
              setUserDetails({
                fullName: '',
                email: '',
                phone: '',
                nationality: '',
                age: '',
                studyDestination: 'uk',
                englishLevel: 'average',
                previousIELTS: 'no',
                previousScore: '',
                fieldOfStudy: '',
                specialRequests: ''
              });
            }}
            className="new-booking-button"
          >
            Book Another Session
          </button>
        </div>
      )}
    </div>
  );
};

export default CalendarBooking;
